import type { TFunction } from 'i18next';
import { formatDate, formatPercent, formatTime, type NotificationSummary } from '@repo/shared';
import { ltr } from './bidi';

type Item = NotificationSummary['items'][number];

const str = (v: unknown) => (typeof v === 'string' ? v : '');
const num = (v: unknown) => (typeof v === 'number' ? v : null);

/**
 * Title and body of a notification in the reader's language. Rows written before Phase 8 (no key) keep
 * their stored French text. Codes in the parameters (weekday, document kind, session, review outcome) are
 * translated here.
 */
export function notificationText(n: Item, t: TFunction): { title: string; body: string } {
  if (!n.key) return { title: n.title, body: n.body };
  const p = n.params ?? {};
  const k = `notifications.${n.key}`;
  const day = num(p.weekday);
  const values = {
    course: str(p.course),
    room: str(p.room),
    groups: str(p.groups),
    level: str(p.level),
    time: ltr(str(p.time)),
    hours: ltr(num(p.hours) ?? ''),
    semester: ltr(num(p.semester) ?? ''),
    pct: ltr(formatPercent(num(p.pct) ?? 0)),
    day: day ? t(`notifications.weekdays.${day}`) : '',
    kind: p.kind ? t(`student.requestKinds.${str(p.kind)}`) : '',
    session: p.session ? t(`notifications.sessions.${str(p.session)}`) : '',
    student: str(p.student),
    outcome: p.outcome ? t(`gradeReviews.status.${str(p.outcome)}`) : '',
    title: str(p.title),
    status: p.status ? t(`internships.status.${str(p.status)}`) : '',
    answer: p.accept ? t(`notifications.internship.answers.${str(p.accept)}`) : '',
    at: p.at ? ltr(`${formatDate(str(p.at))} · ${formatTime(new Date(str(p.at)))}`) : '',
  };
  const title = t(`${k}.title`, values);
  switch (n.key) {
    case 'justification.rejected':
      return { title, body: str(p.reason) || t(`${k}.body`) };
    case 'request.done':
    case 'request.rejected':
      return { title, body: str(p.answer) || t(`${k}.body`) };
    case 'internship.decision':
      return {
        title,
        body: str(p.reason) ? `${t(`${k}.body`, values)} — ${str(p.reason)}` : t(`${k}.body`, values),
      };
    default:
      return { title, body: t(`${k}.body`, values) };
  }
}
